import { strapiAuthProvider, strapiDataProvider, strapiHttpClient } from "ra-strapi";
import { STRAPI_BASE_URL } from '../../../config/services/strapi/strapi';

const authProvider = strapiAuthProvider({
  baseURL: STRAPI_BASE_URL as string,
});
const httpClient = strapiHttpClient();
const dataProvider = strapiDataProvider({
  baseURL: STRAPI_BASE_URL as string,
  httpClient,
});

const strapiToRaRecord = (baseURL: string) => {
  const addBaseURLRecursively: any = (data: any) => {
    if (!data) return;
    if (Array.isArray(data)) {
      return data.map((item) => addBaseURLRecursively(item));
    }
    if (data.hasOwnProperty("url")) {
      const { url, ...rest } = data;
      const updatedRest = Object.entries(rest).reduce((acc: any, [key, value]) => {
        acc[key] =
          typeof value === "object" ? addBaseURLRecursively(value) : value;
        return acc;
      }, {});
      return {
        ...updatedRest,
        url: url.includes("http") ? url : `${baseURL}${url}`,
      };
    }
    return Object.entries(data).reduce((acc: any, [key, value]) => {
      acc[key] =
        typeof value === "object" ? addBaseURLRecursively(value) : value;
      return acc;
    }, {});
  };
  const toRaAttributes = (attributes: any) => {
    Object.keys(attributes).forEach((key: string) => {
      const data = attributes[key];
      if (!data) return;

      if (data.hasOwnProperty("mime")) {
        attributes[key] = addBaseURLRecursively(data);
      }

      // it's a strapi object
      else if (data.hasOwnProperty("documentId")) {
        attributes[key] = addBaseURLRecursively(data);
      }

      // it's an array of strapi objects
      else if (Array.isArray(data) && data.length > 0 && data[0]?.documentId) {
        attributes[key] = data.map((item: any) => addBaseURLRecursively(item));
      }
    });

    return attributes;
  };

  return (data: any) => {
    const { documentId, id, blocks, ...attributes } = data;
    return {
      id: documentId,
      ref: id,
      ...toRaAttributes(attributes),
    };
  };
};

const convertToFormData = (data: any, form: FormData = new FormData(), namespace = ''): FormData => {
  Object.keys(data).forEach((key) => {
    const value = data[key];
    const formKey = namespace ? `${namespace}[${key}]` : key;

    if (value instanceof File) {
      form.append(formKey, value);
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const arrayKey = `${formKey}[${index}]`;

        if (typeof item === 'object' && !(item instanceof File)) {
          convertToFormData(item, form, arrayKey); // handle nested objects
        } else {
          form.append(`${formKey}[]`, item); // handles simple values (like numbers)
        }
      });
    } else if (typeof value === 'object' && value !== null) {
      convertToFormData(value, form, formKey);
    } else if (value !== undefined && value !== null) {
      form.append(formKey, value);
    }
  });

  return form;
};

const getToken = () => {
  try {
    const token = localStorage.getItem('strapi_jwt');
    if (!token) return null;

    return token;
  } catch {
    return null;
  }
};

export const customAuthProvider = {
  ...authProvider,
  login: async (params: any) => {
    await authProvider.login(params);
    return Promise.resolve({
      redirectTo: '/admin/imagenes-inicios'
    });
  }
}

export const customDataProvider = {
  ...dataProvider,

  async getOne(resource: string, params: any) {
    const toRaRecord = strapiToRaRecord(STRAPI_BASE_URL as string);
    const url = `${STRAPI_BASE_URL}/api/${resource}/${params.id}?pLevel`;
    const { data } = await httpClient(url).then((res: any) => res.json);

    const record = {
        id: data.id,
      ...data,
    };

    if (resource === 'podcasts') {
      if (Array.isArray(record.exponente_podcasts)) {
        record.exponente_podcasts = record.exponente_podcasts.map((p: any) => p.documentId);
      }

      return {
        data: toRaRecord(record),
      };
    }

    else if (resource === 'industries') {
      if (Array.isArray(record.soluciones_energeticas)) {
        record.soluciones_energeticas = record.soluciones_energeticas.map((s: any) => s.documentId);
      }

      return {
        data: toRaRecord(record),
      };
    }

    else if (resource === 'solucion-energeticas') {
      if (Array.isArray(record.industrias)) {
        record.industrias = record.industrias.map((i: any) => i.documentId);
      }

      return {
        data: toRaRecord(record),
      };
    }

    return {
      data: toRaRecord(data),
    };
  },

  create: async (resource: string, params: any) => {
    const sentParams = {
      ...params,
    }

    if (resource === 'imagenes-inicios') {
      const toRaRecord = strapiToRaRecord(STRAPI_BASE_URL as string);
      const body = JSON.stringify({
        data: sentParams.data,
      });
      const token = getToken();

      return fetch(`${STRAPI_BASE_URL}/api/${resource}`, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
      })
        .then(response => response.json())
        .then(data => ({ data: toRaRecord(data) }));
    }

    else if (resource === 'solucion-energeticas') {
      const toRaRecord = strapiToRaRecord(STRAPI_BASE_URL as string);
      const body = JSON.stringify({
        data: sentParams.data,
      });

      const token = getToken();

      return fetch(`${STRAPI_BASE_URL}/api/${resource}`, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
      })
        .then(response => response.json())
        .then(data => ({ data: toRaRecord(data) }));
    }

    else if (resource === 'industries') {
      const toRaRecord = strapiToRaRecord(STRAPI_BASE_URL as string);
      const formData = convertToFormData(sentParams.data);
      const token = getToken();

      return fetch(`${STRAPI_BASE_URL}/api/${resource}`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      })
        .then(response => response.json())
        .then(data => ({ data: toRaRecord(data) }));
    }

    else if (resource === 'casos-exitos') {
      const toRaRecord = strapiToRaRecord(STRAPI_BASE_URL as string);
      const body = JSON.stringify({
        data: sentParams.data,
      });
      const token = getToken();

      return fetch(`${STRAPI_BASE_URL}/api/${resource}`, {
        method: 'POST',
        body,
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      })
        .then(response => response.json())
        .then(data => ({ data: toRaRecord(data) }));
    }

    else if (resource === 'articulos') {
      sentParams.data.imagenes_articulo = sentParams.data.contenido.map((component: any, index: number) => {
        if (component.__component === 'shared.media') {
          return component.media ? component.media : null;
        }
      }).filter(Boolean);
    }

    else if (resource === 'podcasts') {
      const toRaRecord = strapiToRaRecord(STRAPI_BASE_URL as string);
      const formData = convertToFormData(sentParams.data);
      const token = getToken();
      
      return fetch(`${STRAPI_BASE_URL}/api/${resource}`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      })
        .then(response => response.json())
        .then(data => ({ data: toRaRecord(data) }));
    }

    return dataProvider.create(resource, sentParams);
  },

  async update(resource: string, params: any) {
    const sentParams = JSON.parse(JSON.stringify(params));

    const toRaRecord = strapiToRaRecord(STRAPI_BASE_URL as string);
    const body = JSON.stringify({
      data: sentParams.data,
    });
    const token = getToken();

    return fetch(`${STRAPI_BASE_URL}/api/${resource}/${params.id}`, {
      method: 'PUT',
      body,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(response => response.json())
      .then(data => ({ data: toRaRecord(data) }));
  }
}